import { useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Dropdown,
  Field,
  Form,
  FormSizes,
  InputStatuses,
  Modal,
  ModalSizes,
  TextInput,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
  useFormApi,
  useFormState,
} from '@carboledger/ui';
import { useGetIsccCertificateQuery } from '@/lib/api/lookupsApi';
import { useDebounce, useTranslations } from '@/hooks';
import { COUNTRY_CODES } from '@/config/countries.constants';
import { buildCountryOptions } from '@/lib/utils';
import {
  useCreateBackToBackAccountMutation,
  useLazyGenerateBackToBackAccountIdQuery,
  useUpdateBackToBackAccountMutation,
} from '../../api/backToBackAccountsApi';
import {
  BACK_TO_BACK_FIELDS,
  EMPTY_BACK_TO_BACK_FORM,
  REQUIRED_BACK_TO_BACK_FIELDS,
} from '../../constants/backToBackAccounts.constants';
import styles from './BackToBackAccountModal.module.scss';

const hasRequiredFields = (values) =>
  REQUIRED_BACK_TO_BACK_FIELDS.every((field) => (values[field] ?? '').trim() !== '');

const awaitsCertificate = (values) =>
  (values[BACK_TO_BACK_FIELDS.isccCertificateNumber] ?? '').trim() !== '' &&
  values[BACK_TO_BACK_FIELDS.isccVerified] !== true;

const TextField = ({ name, label, placeholder, type, required = false, disabled = false }) => (
  <Field name={name} required={required}>
    {({ id, name: inputName, value, onChange, onBlur, status, message }) => (
      <TextInput
        id={id}
        name={inputName}
        type={type}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        status={status}
        message={message}
        required={required}
        disabled={disabled}
      />
    )}
  </Field>
);

/**
 * Props:
 *   disabled  locks the dropdown while the save is in flight
 */
const CountryField = ({ disabled }) => {
  const t = useTranslations();
  // English names, not a site's ISO codes — the declaration PDF prints this value as-is.
  const options = useMemo(() => buildCountryOptions(COUNTRY_CODES), []);

  return (
    <Field name={BACK_TO_BACK_FIELDS.countryName} required>
      {({ id, name: inputName, value, onChange, onBlur, status, message, required }) => (
        <Dropdown
          searchable
          id={id}
          name={inputName}
          label={t('FORM_COUNTRY_NAME')}
          placeholder={t('FORM_SELECT_COUNTRY')}
          searchPlaceholder={t('COMMON_SEARCH_COUNTRY')}
          noResultsMessage={t('COMMON_NO_MATCHES')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
          disabled={disabled}
          options={options}
        />
      )}
    </Field>
  );
};

const AccountIdField = ({ disabled }) => {
  const t = useTranslations();
  const { setValue } = useFormApi();
  const [generateAccountId, { isFetching }] = useLazyGenerateBackToBackAccountIdQuery();

  const handleGenerate = useCallback(async () => {
    const { data } = await generateAccountId();
    if (data) setValue(BACK_TO_BACK_FIELDS.accountId, data);
  }, [generateAccountId, setValue]);

  return (
    <div className={styles.field}>
      <Typography
        variant={TypographyVariants.bodyXs}
        weight={TypographyWeights.semibold}
        color={TypographyColors.secondary}
      >
        {t('FORM_ACCOUNT_ID')}
        <span className={styles.required} aria-hidden="true">
          {' '}
          *
        </span>
      </Typography>

      <div className={styles.controls}>
        <Field name={BACK_TO_BACK_FIELDS.accountId} required>
          {({ id, name, value, onChange, onBlur, status, message }) => (
            <TextInput
              className={styles.input}
              id={id}
              name={name}
              aria-label={t('FORM_ACCOUNT_ID')}
              placeholder={t('FORM_ACCOUNT_ID_PLACEHOLDER')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              disabled={disabled}
            />
          )}
        </Field>

        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.md}
          onClick={handleGenerate}
          disabled={disabled || isFetching}
        >
          {t('FORM_GENERATE')}
        </Button>
      </div>
    </div>
  );
};

const IsccCertificateField = ({ disabled }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const typed = (values[BACK_TO_BACK_FIELDS.isccCertificateNumber] ?? '').trim();
  const certificateNumber = useDebounce(typed);
  const { data, isFetching, isError } = useGetIsccCertificateQuery(certificateNumber, {
    skip: !certificateNumber,
  });

  useEffect(() => {
    if (!certificateNumber || !data) {
      setValue(BACK_TO_BACK_FIELDS.isccVerified, false);
      setValue(BACK_TO_BACK_FIELDS.validFrom, '');
      setValue(BACK_TO_BACK_FIELDS.validTill, '');
      return;
    }
    setValue(BACK_TO_BACK_FIELDS.isccVerified, true);
    setValue(BACK_TO_BACK_FIELDS.validFrom, data.validFrom);
    setValue(BACK_TO_BACK_FIELDS.validTill, data.validTo);
  }, [certificateNumber, data, setValue]);

  const lookupMessage = useMemo(() => {
    if (isFetching) return t('SITES_CERTIFICATE_LOOKUP_CHECKING');
    if (isError) return t('SITES_CERTIFICATE_LOOKUP_NOT_FOUND');
    return undefined;
  }, [isFetching, isError, t]);

  return (
    <Field name={BACK_TO_BACK_FIELDS.isccCertificateNumber}>
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={t('FORM_ISCC_CERTIFICATE_NUMBER_OPTIONAL')}
          placeholder={t('FORM_ISCC_CERTIFICATE_NUMBER_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={message ? status : isError ? InputStatuses.error : InputStatuses.default}
          message={message ?? lookupMessage}
          disabled={disabled}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   open     whether the modal is shown
 *   onClose  close handler (✕ / Cancel)
 *   account  the row being edited; omit to add a new account
 */
export const BackToBackAccountModal = ({ open, onClose, account }) => {
  const t = useTranslations();
  const [createAccount, { isLoading: isCreating }] = useCreateBackToBackAccountMutation();
  const [updateAccount, { isLoading: isUpdating }] = useUpdateBackToBackAccountMutation();

  const isEdit = Boolean(account);
  const saving = isCreating || isUpdating;

  const initialValues = useMemo(
    () => account?.formValues ?? EMPTY_BACK_TO_BACK_FORM,
    [account?.formValues],
  );

  const handleSubmit = useCallback(
    async (values) => {
      const result = isEdit
        ? await updateAccount({ id: account.id, values })
        : await createAccount(values);

      if (result.error) return;
      onClose();
    },
    [isEdit, account?.id, createAccount, updateAccount, onClose],
  );

  if (!open) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ values, submit }) => (
        <Modal
          open
          size={ModalSizes.medium}
          title={isEdit ? t('BACK_TO_BACK_EDIT_TITLE') : t('BACK_TO_BACK_ADD')}
          onClose={onClose}
          buttons={[
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
            {
              label: isEdit ? t('COMMON_SAVE') : t('COMMON_ADD'),
              variant: ButtonVariants.primary,
              disabled: saving || !hasRequiredFields(values) || awaitsCertificate(values),
              onClick: submit,
            },
          ]}
        >
          <div className={styles.fields}>
            <Form.Row columns={2}>
              <TextField
                required
                name={BACK_TO_BACK_FIELDS.accountName}
                label={t('FORM_ACCOUNT_NAME')}
                placeholder={t('FORM_ACCOUNT_NAME_PLACEHOLDER')}
                disabled={saving}
              />
              <AccountIdField disabled={saving} />
            </Form.Row>

            <IsccCertificateField disabled={saving} />

            <Form.Row columns={2}>
              <TextField
                disabled
                type="date"
                name={BACK_TO_BACK_FIELDS.validFrom}
                label={t('FIELD_VALID_FROM')}
                placeholder={t('FIELD_VALID_FROM')}
              />
              <TextField
                disabled
                type="date"
                name={BACK_TO_BACK_FIELDS.validTill}
                label={t('FIELD_VALID_TILL')}
                placeholder={t('FIELD_VALID_TILL')}
              />
            </Form.Row>

            <div className={styles.sectionHeader}>
              <Typography
                variant={TypographyVariants.label}
                weight={TypographyWeights.semibold}
                color={TypographyColors.secondary}
              >
                {t('SECTION_DECLARATION_DETAILS')}
              </Typography>
              <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.placeholder}>
                {t('BACK_TO_BACK_DECLARATION_HINT')}
              </Typography>
            </div>

            <TextField
              required
              name={BACK_TO_BACK_FIELDS.officialSiteName}
              label={t('FIELD_OFFICIAL_SITE_NAME')}
              placeholder={t('FORM_OFFICIAL_SITE_NAME_PLACEHOLDER')}
              disabled={saving}
            />

            <Form.Row columns={2}>
              <TextField
                required
                name={BACK_TO_BACK_FIELDS.addressLine1}
                label={t('FORM_ADDRESS_LINE_1')}
                placeholder={t('FORM_ADDRESS_LINE_1_PLACEHOLDER')}
                disabled={saving}
              />
              <TextField
                name={BACK_TO_BACK_FIELDS.addressLine2}
                label={t('FORM_ADDRESS_LINE_2')}
                placeholder={t('FORM_ADDRESS_LINE_2_PLACEHOLDER')}
                disabled={saving}
              />
            </Form.Row>

            <Form.Row columns={2}>
              <TextField
                required
                name={BACK_TO_BACK_FIELDS.cityName}
                label={t('FORM_CITY_NAME')}
                placeholder={t('FORM_CITY_NAME_PLACEHOLDER')}
                disabled={saving}
              />
              <CountryField disabled={saving} />
            </Form.Row>

            <Form.Row columns={2}>
              <TextField
                required
                name={BACK_TO_BACK_FIELDS.zipCode}
                label={t('FORM_ZIP_CODE')}
                placeholder={t('FORM_ZIP_CODE_PLACEHOLDER')}
                disabled={saving}
              />
            </Form.Row>
          </div>
        </Modal>
      )}
    </Form>
  );
};
